import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Card, Form, InputNumber, Select, DatePicker, notification, Typography, Space, Tag, Row, Col, Checkbox } from 'antd';
import { PlusOutlined, SyncOutlined, ThunderboltOutlined, DownloadOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { date as fmtDate } from '../../lib/format';
/** VAT setup of one set of books: VAT codes, transaction codes, the VAT ledger and the ScanNRead sync. */
export default function VatSetupPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const canImport = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId, ledger } = useBooks();
  const [showTemplate, setShowTemplate] = React.useState(false);
  const [vatCodes, setVatCodes] = React.useState([]);
  const [transactionCodes, setTransactionCodes] = React.useState([]);
  const [entries, setEntries] = React.useState([]);
  const [entryModalOpen, setEntryModalOpen] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [entryForm] = Form.useForm();

  const scope = showTemplate && isSystemAdmin ? { template: '1' } : ledgerId ? { ledger_id: ledgerId } : null;

  const fetchAll = () => {
    if (!scope) {
      setVatCodes([]);
      setTransactionCodes([]);
      setEntries([]);
      return;
    }
    request.get('/vat-setup/vat-codes', scope).then((r) => r.json()).then((r) => r.status && setVatCodes(r.data));
    request.get('/vat-setup/transaction-codes', scope).then((r) => r.json()).then((r) => r.status && setTransactionCodes(r.data));
    if (scope.template) setEntries([]);
    else request.get('/vat-setup/entries', scope).then((r) => r.json()).then((r) => r.status && setEntries(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [ledgerId, showTemplate]);

  const handleBootstrap = () => {
    request
      .post('/vat-setup/bootstrap', {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: 'ADMIN template bootstrapped' });
          fetchAll();
        } else notification.error({ message: r.message });
      });
  };

  const handleImportDefaults = () => {
    if (!ledgerId) return notification.error({ message: 'Choose the books first' });
    setImporting(true);
    request
      .post(`/ledgers/${ledgerId}/import-defaults`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          const totalCreated = ['vatCodes', 'transactionCodes', 'vatFormLines'].reduce((sum, k) => sum + (r.data[k]?.created?.length || 0), 0);
          notification.success({ message: `Imported ${totalCreated} new VAT / transaction row(s) from the ADMIN template (already-imported rows were skipped).` });
          fetchAll();
        } else notification.error({ message: r.message });
      })
      .finally(() => setImporting(false));
  };

  const handleSync = () => {
    if (!ledgerId) return notification.error({ message: 'Choose the books first' });
    setSyncing(true);
    request
      .post(`/ledgers/${ledgerId}/sync`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Synced: ${r.data.documentsScanned} documents, ${r.data.entriesCreated} created, ${r.data.entriesUpdated} updated, ${r.data.needsReview} need review` });
          fetchAll();
        } else notification.error({ message: r.message });
      })
      .finally(() => setSyncing(false));
  };

  const handleAddEntry = () => {
    entryForm.validateFields().then((values) => {
      request
        .post('/vat-setup/entries', { ...values, ledger_id: ledgerId, transaction_date: values.transaction_date.format('YYYY-MM-DD') })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Entry added' });
            setEntryModalOpen(false);
            fetchAll();
          } else notification.error({ message: r.message });
        });
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>VAT Setup</Typography.Title>
          {scope && scope.template ? <Tag color="purple">ADMIN template</Tag> : <BooksCaption />}
        </div>
        <Space wrap>
          <BooksSelector />
          {isSystemAdmin && <Checkbox checked={showTemplate} onChange={(e) => setShowTemplate(e.target.checked)}>ADMIN template</Checkbox>}
        </Space>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        {isSystemAdmin && showTemplate && (
          <Button icon={<ThunderboltOutlined />} onClick={handleBootstrap}>
            Bootstrap ADMIN Template
          </Button>
        )}
        {canImport && !showTemplate && ledger && (
          <Button icon={<DownloadOutlined />} onClick={handleImportDefaults} loading={importing}>
            Import template rows into these books
          </Button>
        )}
      </Space>

      <Row gutter={16}>
        <Col span={12}>
          <Card title={`VAT Codes (${vatCodes.length})`} size="small" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 10, size: 'small' }}
              dataSource={vatCodes}
              columns={[
                { title: 'Code', dataIndex: 'code', render: (v) => <Tag>{v}</Tag> },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Rate', dataIndex: 'rate', render: (v) => `${v}%` },
                { title: 'Country', dataIndex: 'country', render: (v) => v || '-' },
              ]}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Transaction Codes" size="small" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={transactionCodes}
              columns={[
                { title: 'Code', dataIndex: 'code', render: (v) => <Tag>{v}</Tag> },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Polarity', dataIndex: 'polarity' },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {!showTemplate && (
        <Card title="ScanNRead (scanned-document intake)" size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Button type="primary" icon={<SyncOutlined spin={syncing} />} onClick={handleSync} loading={syncing} disabled={!ledger || !ledger.has_scan_n_read}>
              Sync Now
            </Button>
            <Typography.Text type="secondary">
              {ledger && ledger.has_scan_n_read ? `These books read tenant ${ledger.scan_n_read_customer_id}.` : 'These books are not connected to a ScanNRead tenant yet.'} The connection is set under <Link to="/books">Client Books → Settings</Link>.
            </Typography.Text>
          </Space>
        </Card>
      )}

      {!showTemplate && (
        <Card
          title="VAT Ledger Entries"
          size="small"
          extra={
            <Button size="small" icon={<PlusOutlined />} onClick={() => setEntryModalOpen(true)} disabled={!ledgerId}>
              Manual Entry
            </Button>
          }
        >
          <Table
            size="small"
            rowKey="id"
            dataSource={entries}
            columns={[
              { title: 'Date', dataIndex: 'transaction_date', render: (v) => fmtDate(v) },
              { title: 'Source', dataIndex: 'source', render: (v) => <Tag color={v === 'SCAN_N_READ' ? 'blue' : v === 'JOURNAL' ? 'purple' : v === 'INVOICE' ? 'green' : 'default'}>{v}</Tag> },
              { title: 'Net', dataIndex: 'net_amount' },
              { title: 'VAT', dataIndex: 'vat_amount' },
              { title: 'Counterparty', dataIndex: 'counterparty_name' },
              { title: 'Country', dataIndex: 'counterparty_country' },
              { title: 'Review?', dataIndex: 'needs_review', render: (v) => (v ? <Tag color="red">Needs Review</Tag> : '') },
            ]}
          />
        </Card>
      )}

      {entryModalOpen && (
        <Card style={{ marginTop: 16 }} title="New Manual Entry">
          <Form form={entryForm} layout="vertical" style={{ maxWidth: 400 }}>
            <Form.Item name="transaction_date" label="Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="transaction_code_id" label="Transaction Code" rules={[{ required: true }]}>
              <Select options={transactionCodes.map((t) => ({ value: t.id, label: `${t.code} - ${t.name}` }))} />
            </Form.Item>
            <Form.Item name="vat_code_id" label="VAT Code" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={vatCodes.map((v) => ({ value: v.id, label: `${v.code} - ${v.name}` }))} />
            </Form.Item>
            <Form.Item name="net_amount" label="Net Amount" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="vat_amount" label="VAT Amount" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Space>
              <Button type="primary" onClick={handleAddEntry}>
                Save
              </Button>
              <Button onClick={() => setEntryModalOpen(false)}>Cancel</Button>
            </Space>
          </Form>
        </Card>
      )}
    </div>
  );
}
